import {
  AdminAssignTransactionInvoice,
  AdminAuthorizeTransaction,
  AdminFetchMissingInvoiceTransactions,
  AdminFetchPartnerPendingInvoices,
  AdminFetchPendingReviewTransactions,
  AdminRefundTransaction,
} from 'src/constants/actionTypes'
import type {
  FetchMissingInvoiceTransactionsPayload,
  FetchPartnerPendingInvoicesPayload,
  MissingInvoiceTransactionsPage,
  PartnerInvoice,
  PaymentsOpsTransaction,
} from 'src/interfaces/transactionsReview'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { Action } from 'src/utils/reduxUtils'
import type { TransactionsReviewState } from './types'

const buildIncomingKey = (payload?: FetchMissingInvoiceTransactionsPayload): string => {
  return `${payload?.partnerId ?? ''}|${payload?.externalTransactionId ?? ''}|${Boolean(
    payload?.activeOnly,
  )}`
}

const getInitialState = (): TransactionsReviewState => {
  return {
    incoming: [],
    incomingTotal: 0,
    incomingLoadedKey: null,
    incomingRequestedKey: null,
    incomingLoading: false,
    incomingError: null,
    outgoing: [],
    outgoingLoading: false,
    outgoingError: null,
    pendingInvoices: [],
    pendingInvoicesPartnerId: null,
    pendingInvoicesLoading: false,
    pendingInvoicesError: null,
    submitting: false,
  }
}

const WRITE_REQUEST_TYPES = [
  AdminAssignTransactionInvoice.type.REQUEST,
  AdminRefundTransaction.type.REQUEST,
  AdminAuthorizeTransaction.type.REQUEST,
]

const WRITE_SETTLED_TYPES = [
  AdminAssignTransactionInvoice.type.SUCCESS,
  AdminAssignTransactionInvoice.type.FAILED,
  AdminRefundTransaction.type.SUCCESS,
  AdminRefundTransaction.type.FAILED,
  AdminAuthorizeTransaction.type.SUCCESS,
  AdminAuthorizeTransaction.type.FAILED,
]

export const transactionsReviewReducer = (
  state: TransactionsReviewState = getInitialState(),
  action: Action<unknown>,
): TransactionsReviewState => {
  if (WRITE_REQUEST_TYPES.includes(action.type)) {
    return { ...state, submitting: true }
  }

  if (WRITE_SETTLED_TYPES.includes(action.type)) {
    return { ...state, submitting: false }
  }

  switch (action.type) {
    case AdminFetchMissingInvoiceTransactions.type.REQUEST: {
      const requestedKey = buildIncomingKey(
        action.payload as FetchMissingInvoiceTransactionsPayload | undefined,
      )
      const isSameKey = requestedKey === state.incomingLoadedKey

      return {
        ...state,
        incoming: isSameKey ? state.incoming : [],
        incomingTotal: isSameKey ? state.incomingTotal : 0,
        incomingLoadedKey: isSameKey ? state.incomingLoadedKey : null,
        incomingRequestedKey: requestedKey,
        incomingLoading: true,
        incomingError: null,
      }
    }
    case AdminFetchMissingInvoiceTransactions.type.SUCCESS: {
      const page = action.payload as MissingInvoiceTransactionsPage | undefined

      return {
        ...state,
        incoming: page?.data ?? [],
        incomingTotal: page?.total ?? 0,
        incomingLoadedKey: state.incomingRequestedKey,
        incomingLoading: false,
        incomingError: null,
      }
    }
    case AdminFetchMissingInvoiceTransactions.type.FAILED:
      return {
        ...state,
        incomingLoading: false,
        incomingError: resolveErrorMessage(action.payload, 'Failed to load incoming transactions'),
      }

    case AdminFetchPendingReviewTransactions.type.REQUEST:
      return { ...state, outgoingLoading: true, outgoingError: null }
    case AdminFetchPendingReviewTransactions.type.SUCCESS:
      return {
        ...state,
        outgoing: (action.payload as PaymentsOpsTransaction[] | undefined) ?? [],
        outgoingLoading: false,
        outgoingError: null,
      }
    case AdminFetchPendingReviewTransactions.type.FAILED:
      return {
        ...state,
        outgoingLoading: false,
        outgoingError: resolveErrorMessage(action.payload, 'Failed to load outgoing transactions'),
      }

    case AdminFetchPartnerPendingInvoices.type.REQUEST: {
      const partnerId =
        (action.payload as FetchPartnerPendingInvoicesPayload | undefined)?.partnerId ?? null
      const isSamePartner = partnerId === state.pendingInvoicesPartnerId

      return {
        ...state,
        pendingInvoices: isSamePartner ? state.pendingInvoices : [],
        pendingInvoicesPartnerId: partnerId,
        pendingInvoicesLoading: true,
        pendingInvoicesError: null,
      }
    }
    case AdminFetchPartnerPendingInvoices.type.SUCCESS:
      return {
        ...state,
        pendingInvoices: (action.payload as PartnerInvoice[] | undefined) ?? [],
        pendingInvoicesLoading: false,
        pendingInvoicesError: null,
      }
    // A failed load must not leave the previous load's invoices behind: they may since have been
    // paid (e.g. by the assignment that preceded this reopen) and would still be assignable.
    case AdminFetchPartnerPendingInvoices.type.FAILED:
      return {
        ...state,
        pendingInvoices: [],
        pendingInvoicesLoading: false,
        pendingInvoicesError: resolveErrorMessage(action.payload, 'Failed to load open invoices'),
      }

    default:
      return state
  }
}
